// Acuerdos de Nivel de Servicio (SLA). Dedicated home for SLA management,
// moved out of Configuración: objetivos de 1ª respuesta y resolución por
// prioridad (globales), overrides por marca y el umbral "en riesgo".
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { brand as themeBrand } from "../../../config/theme";
import { getError } from "../../../utils/getError";
import {
  getSettings, updateSettings, formatMins,
  type ServiceDeskSettings, type TicketPriority, type SlaTarget,
} from "../services/serviceDeskService";
import { DeskLoadErrorPanel, useDeskLoadError } from "../components/DeskLoadError";

const PRIORITIES: TicketPriority[] = ["urgent", "high", "medium", "low"];
const PRIORITY_LABELS: Record<TicketPriority, string> = { urgent: "Urgente", high: "Alta", medium: "Media", low: "Baja" };

const emptyTarget = (): SlaTarget => ({ firstResponseMins: 0, resolutionMins: 0 });

export default function SlasSection() {
  const { t } = useTranslation("serviceDesk");
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [brandId, setBrandId] = useState<string>(""); // "" = objetivos globales
  // GET /serviceDesk/settings también exige rol de agente: sin capturar el 403
  // la pantalla se quedaba en "Cargando…" indefinidamente.
  const { error: loadError, capture } = useDeskLoadError();

  useEffect(() => { getSettings().then(setSettings).catch(capture); }, [capture]);

  const card = "bg-surface border-line";
  const text = "text-content";
  const muted = "text-content-muted";

  if (loadError) return <DeskLoadErrorPanel error={loadError} />;
  if (!settings) return <div className="p-8 text-center text-gray-400">{t("dashboard.loading")}</div>;

  // Targets currently being edited: global slaTargets, or the per-brand override.
  const targetsFor = (b: string): Record<string, SlaTarget> =>
    b ? (settings.slaByBrand?.[b] ?? {}) : (settings.slaTargets || {});
  const current = targetsFor(brandId);

  const setTarget = (p: TicketPriority, key: keyof SlaTarget, value: number) => {
    setSettings((s) => {
      if (!s) return s;
      const next = { ...(targetsFor(brandId)[p] ?? emptyTarget()), [key]: value };
      if (!brandId) {
        return { ...s, slaTargets: { ...s.slaTargets, [p]: next } };
      }
      const brandMap = { ...(s.slaByBrand ?? {}) };
      brandMap[brandId] = { ...(brandMap[brandId] ?? {}), [p]: next };
      return { ...s, slaByBrand: brandMap };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateSettings({
        slaTargets: settings.slaTargets,
        slaByBrand: settings.slaByBrand,
        slaRiskThreshold: settings.slaRiskThreshold,
      });
      setSettings(updated);
      Swal.fire({ icon: "success", title: t("slas.saved"), timer: 1200, showConfirmButton: false });
    } catch (err) {
      // Guardar la configuración exige rol de agente de la mesa: sin este aviso
      // el 403 quedaba en una promesa sin capturar.
      Swal.fire({ icon: "error", title: t("slas.saveError"), text: getError(err) });
    } finally {
      setSaving(false);
    }
  };

  const clearBrandOverride = () => {
    if (!brandId) return;
    setSettings((s) => {
      if (!s) return s;
      const brandMap = { ...(s.slaByBrand ?? {}) };
      delete brandMap[brandId];
      return { ...s, slaByBrand: brandMap };
    });
  };

  const brandHasOverride = (b: string) => Boolean(settings.slaByBrand?.[b] && Object.keys(settings.slaByBrand[b]).length);

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${text}`}>{t("slas.title")}</h1>
          <p className={`text-sm ${muted}`}>{t("slas.description")}</p>
        </div>
        <select
          className={`rounded-lg border px-3 py-1.5 text-sm !bg-app !border-line !text-content`}
          value={brandId}
          onChange={(e) => setBrandId(e.target.value)}
        >
          <option value="">{t("slas.globalTargets")}</option>
          {(settings.brands || []).map((b) => (
            <option key={b.id} value={b.id}>{b.label}{brandHasOverride(b.id) ? " ✓" : ""}</option>
          ))}
        </select>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className={`rounded-xl border p-5 ${card}`}>
          <div className="mb-3 flex items-center justify-between">
            <h2 className={`text-sm font-semibold ${text}`}>
              {brandId ? `${t("slas.brandTargets")} ${(settings.brands || []).find((b) => b.id === brandId)?.label ?? brandId}` : t("slas.globalTargets")} (minutos)
            </h2>
            {brandId && brandHasOverride(brandId) && (
              <button onClick={clearBrandOverride} className={`text-xs text-error hover:underline`}>{t("slas.useGlobal")}</button>
            )}
          </div>
          {brandId && !brandHasOverride(brandId) && (
            <p className={`mb-3 text-xs ${muted}`}>{t("slas.noOverride")}</p>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left text-xs uppercase ${muted}`}>
                <th className="py-1">{t("slas.table.priority")}</th>
                <th className="py-1">{t("slas.table.firstResponse")}</th>
                <th className="py-1">{t("slas.table.resolution")}</th>
              </tr>
            </thead>
            <tbody>
              {PRIORITIES.map((p) => {
                const fr = current[p]?.firstResponseMins ?? 0;
                const rs = current[p]?.resolutionMins ?? 0;
                return (
                  <tr key={p}>
                    <td className={`py-1.5 ${text}`}>{PRIORITY_LABELS[p]}</td>
                    <td className="py-1.5">
                      <input type="number" min="0" className={numCls} value={fr} onChange={(e) => setTarget(p, "firstResponseMins", Number(e.target.value))} />
                      <span className={`ml-2 text-xs ${muted}`}>{formatMins(fr || null)}</span>
                    </td>
                    <td className="py-1.5">
                      <input type="number" min="0" className={numCls} value={rs} onChange={(e) => setTarget(p, "resolutionMins", Number(e.target.value))} />
                      <span className={`ml-2 text-xs ${muted}`}>{formatMins(rs || null)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("slas.info.title")}</h2>
          <p className={`mb-3 text-xs ${muted}`}>{t("slas.info.description")}</p>
          <input
            type="number" step="0.05" min="0" max="1" className={numCls}
            value={settings.slaRiskThreshold ?? 0.8}
            onChange={(e) => setSettings((s) => s && ({ ...s, slaRiskThreshold: Number(e.target.value) }))}
          />
          <ul className={`mt-5 space-y-1.5 text-xs ${muted} list-disc pl-4`}>
            <li>{t("slas.info.features.0")}</li>
            <li>{t("slas.info.features.1")}</li>
            <li>{t("slas.info.features.2")}</li>
          </ul>
        </div>
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="mt-5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 hover:opacity-90"
        style={{ backgroundColor: themeBrand.primary }}
      >
        {t("slas.save")}
      </button>
    </div>
  );
}

const numCls = "w-28 rounded-lg border px-2 py-1 text-sm";
